import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useQueryParams } from "@/hooks/use-query-param";
import {
  nameIssue,
  REQUIRED_FIELD_MESSAGE,
} from "@/constants/validation-constant";
import { useSnackbar } from "@/hooks/useSnackbar";
import { usePatientAllergiesCreate } from "@/queries/allergies/use-patient-allergies-create";
import { usePatientAllergiesUpdate } from "@/queries/allergies/use-patient-allergies-update";
import { useArchiveAllergy } from "@/queries/allergies/use-patient-allergy-archive";
import { PatientAllergyControllerApi, Patientallergy } from "@/services";
import {
  asPaginatedContent,
  getConfigObject,
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "@/utils/utils";
import { AllergyRow } from "../readmission-f2f-types";
import {
  createAllergyRow,
  isSameAllergyRow,
  mapAllergyToRow,
  recordChangedSnackbar,
} from "../readmission-f2f-utils";
import {
  ALLERGY_RECORD_NAME,
  ALLERGY_LIST_PAGE_SIZE,
  PATIENT_ALLERGY_LIST_KEY,
} from "../readmission-f2f-constant";

export interface AllergyDraftError {
  allergy?: string;
  severity?: string;
}

export interface UseAllergySection {
  rows: AllergyRow[];
  draft: AllergyRow;
  /** Set when Add was pressed with an invalid name or no severity; each clears on its next edit. */
  draftError?: AllergyDraftError;
  isLoading: boolean;
  isSaving: boolean;
  canAddDraft: boolean;
  updateRow: (id: string, patch: Partial<AllergyRow>) => void;
  updateDraft: (patch: Partial<AllergyRow>) => void;
  addDraft: () => Promise<void>;
  saveRow: (row: AllergyRow) => Promise<void>;
  removeRow: (row: AllergyRow) => void;
}

export const useAllergySection = (): UseAllergySection => {
  const { patientId } = useParams() as { patientId?: string };
  const appointmentId = useQueryParams("appointmentid");
  const xTENANTID = getXtenantId();
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();

  const { mutateAsync: createAllergy, isPending: isCreating } =
    usePatientAllergiesCreate();
  const { mutateAsync: updateAllergy, isPending: isUpdating } =
    usePatientAllergiesUpdate();
  const { mutateAsync: archiveAllergy, isPending: isArchiving } =
    useArchiveAllergy();

  const { data: serverContent, isLoading } = useQuery({
    queryKey: [PATIENT_ALLERGY_LIST_KEY, patientId, appointmentId, xTENANTID],
    refetchOnWindowFocus: false,
    enabled: !!patientId || !!appointmentId,
    queryFn: () =>
      new PatientAllergyControllerApi(getConfigObject())
        .getPatientAllergy(
          0,
          ALLERGY_LIST_PAGE_SIZE,
          undefined,
          undefined,
          patientId,
          appointmentId ?? undefined,
          true,
          false,
          undefined,
          undefined,
          xTENANTID
        )
        .then(
          (res) =>
            asPaginatedContent<Patientallergy>(res?.data)?.data?.content ?? []
        ),
  });

  const serverRows = useMemo(
    () => (serverContent ?? []).map(mapAllergyToRow),
    [serverContent]
  );
  const serverById = useMemo(() => {
    const map = new Map<string, AllergyRow>();
    serverRows.forEach((row) => map.set(row.id, row));
    return map;
  }, [serverRows]);

  const [rows, setRows] = useState<AllergyRow[]>([]);
  const signature = useMemo(
    () =>
      serverRows
        .map((r) => `${r.uuid}|${r.allergy}|${r.severity}|${r.notes}`)
        .join("~"),
    [serverRows]
  );
  const lastSignature = useRef<string | null>(null);
  useEffect(() => {
    if (lastSignature.current !== signature) {
      lastSignature.current = signature;
      setRows(serverRows);
    }
  }, [signature, serverRows]);

  const invalidate = useCallback(
    () =>
      queryClient.invalidateQueries({ queryKey: [PATIENT_ALLERGY_LIST_KEY] }),
    [queryClient]
  );

  // Update replaces the whole record, so keep the fields this row does not edit.
  const buildPayload = useCallback(
    (row: AllergyRow) => ({
      patientAllergy: {
        ...serverContent?.find((it) => it.uuid === row.uuid),
        uuid: row.uuid,
        allergy: row.allergy.trim(),
        severity: row.severity || undefined,
        note: row.notes.trim() || undefined,
        active: true,
        patient: { [patientId ?? ""]: "" },
        appointmentId: appointmentId ?? undefined,
      } as Patientallergy,
      xTENANTID,
    }),
    [serverContent, patientId, appointmentId, xTENANTID]
  );

  const updateRow = useCallback((id: string, patch: Partial<AllergyRow>) => {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );
  }, []);

  const [draft, setDraft] = useState<AllergyRow>(() => createAllergyRow());

  const [draftError, setDraftError] = useState<AllergyDraftError>();

  const updateDraft = useCallback((patch: Partial<AllergyRow>) => {
    setDraft((prev) => ({ ...prev, ...patch }));
    setDraftError((prev) =>
      prev
        ? {
            allergy: patch.allergy !== undefined ? undefined : prev.allergy,
            severity: patch.severity !== undefined ? undefined : prev.severity,
          }
        : prev
    );
  }, []);

  const addDraft = useCallback(async () => {
    const issue: AllergyDraftError = {
      allergy: nameIssue(draft.allergy),
      severity: draft.severity ? undefined : REQUIRED_FIELD_MESSAGE,
    };
    if (issue.allergy || issue.severity) {
      setDraftError(issue);
      return;
    }
    try {
      await createAllergy(buildPayload(draft));
      showSnackbar(recordChangedSnackbar(ALLERGY_RECORD_NAME, "added"));
      setDraft(createAllergyRow());
      await invalidate();
    } catch (err) {
      showSnackbar(getErrorSnackbar(err));
    }
  }, [draft, createAllergy, buildPayload, invalidate, showSnackbar]);

  const saveRow = useCallback(
    async (row: AllergyRow) => {
      if (!row.uuid || nameIssue(row.allergy)) return;
      const original = serverById.get(row.id);
      if (original && isSameAllergyRow(original, row)) return;
      try {
        const res = await updateAllergy(buildPayload(row));
        showSnackbar(getSuccessSnackbar(res));
        await invalidate();
      } catch (err) {
        showSnackbar(getErrorSnackbar(err));
      }
    },
    [serverById, updateAllergy, buildPayload, invalidate, showSnackbar]
  );

  const removeRow = useCallback(
    async (row: AllergyRow) => {
      if (!row.uuid) {
        setRows((prev) => prev.filter((it) => it.id !== row.id));
        return;
      }
      try {
        await archiveAllergy({
          patientAllergyId: row.uuid,
          status: true,
          xTENANTID,
        });
        showSnackbar(recordChangedSnackbar(ALLERGY_RECORD_NAME, "removed"));
        await invalidate();
      } catch (err) {
        showSnackbar(getErrorSnackbar(err));
      }
    },
    [archiveAllergy, xTENANTID, invalidate, showSnackbar]
  );

  return {
    rows,
    draft,
    draftError,
    isLoading,
    isSaving: isCreating || isUpdating || isArchiving,
    canAddDraft: !!draft.allergy.trim(),
    updateRow,
    updateDraft,
    addDraft,
    saveRow,
    removeRow,
  };
};
